import type React from "react";
import { useState } from "react";

import { PERIOD_OPTIONS, type Policy, type Program } from "../../shared/programMocks";
import { serializeTranches } from "../programHelpers";
import type { BuilderConfig } from "../Programs";

/**
 * useDraftProgram — the draft-builder logic layer for `ProgramDetail`.
 *
 * Holds the local draft state seeded from the passed `program` (config
 * fields + `policies` / `policyValues` / `budget`), the policy handlers
 * (add / remove / setValue), and `toBuilderConfig()` — the assembly the
 * confirm bar used to inline. Funding is NOT stored here: it's derived from
 * the Budget Type and shown as a read-only Configuration field. The
 * returned field + setter names match the prop interfaces the section DCs
 * already receive, so `ProgramDetail` just spreads them down.
 *
 * Behavior-identical to the previous inline `useState` block: same initial
 * values, same setters, same `BuilderConfig` shape.
 */
// Backend-contract defaults for fields removed from the Configuration UI
// (Eligible Region / Target / Rewards Model). The org-API's required policies
// still expect them, so the builder sends these fixed values.
const DEFAULT_REGION = "global";
const DEFAULT_TARGET: "agents" | "holders" = "agents";
const DEFAULT_REWARDS_PCT = 100;
// Default share of earnings passed to the user for a Variable (earned) budget.
// A fresh draft opens on a full 100% user split (revenue split — the whole
// earned yield is passed through) matching createDraftProgram's userPct.
const DEFAULT_USER_PCT = 100;

export interface DraftProgram {
    name: string;
    setName: (name: string) => void;
    programType: "fixed" | "continuous";
    setProgramType: React.Dispatch<React.SetStateAction<"fixed" | "continuous">>;
    budgetMode: "fixed" | "open";
    setBudgetMode: React.Dispatch<React.SetStateAction<"fixed" | "open">>;
    /** For open-budget / continuous programs: whether the APY is the variable
     *  earned APY or a fixed base APY. `base` reveals the Base APY % input. */
    apyMode: "earned" | "base";
    setApyMode: React.Dispatch<React.SetStateAction<"earned" | "base">>;
    baseApy: number;
    setBaseApy: React.Dispatch<React.SetStateAction<number>>;
    /** Variable earned budget: the % share of earnings passed to the user. */
    userPct: number;
    setUserPct: React.Dispatch<React.SetStateAction<number>>;
    periodDays: number;
    setPeriodDays: React.Dispatch<React.SetStateAction<number>>;
    settlementCycleLength: number;
    setSettlementCycleLength: React.Dispatch<React.SetStateAction<number>>;
    settlementCycleUnit: "days" | "weeks";
    setSettlementCycleUnit: React.Dispatch<React.SetStateAction<"days" | "weeks">>;
    budget: number;
    setBudget: React.Dispatch<React.SetStateAction<number>>;
    policies: Policy[];
    policyValues: Record<string, number | string>;
    addPolicy: (policy: Policy, value: number | string) => void;
    removePolicy: (id: string) => void;
    setPolicyValue: (id: string, value: number | string) => void;
    toBuilderConfig: () => BuilderConfig;
}

export function useDraftProgram(program: Program): DraftProgram {
    const [name, setName] = useState<string>(program.name);
    // Default a fresh draft to Continuous · Variable (earned) — the fallbacks
    // match createDraftProgram so the builder opens the same way whether the
    // program carries explicit config or not.
    const [programType, setProgramType] = useState<"fixed" | "continuous">(program.programType ?? "continuous");
    const [budgetMode, setBudgetMode] = useState<"fixed" | "open">(program.budgetMode ?? "open");
    // Seed the APY mode from the seeded program: a non-zero baseApy reads as a
    // fixed base APY, otherwise the variable earned APY.
    const [apyMode, setApyMode] = useState<"earned" | "base">((program.baseApy ?? 0) > 0 ? "base" : "earned");
    const [baseApy, setBaseApy] = useState(program.baseApy ?? 8);
    // User % (Variable earned budget) — seed from the program's userPct, else
    // its rewardsPct (same "share to users" semantics), else the 80% default.
    const [userPct, setUserPct] = useState(program.userPct ?? program.rewardsPct ?? DEFAULT_USER_PCT);
    const [periodDays, setPeriodDays] = useState(
        program.periodDays ?? PERIOD_OPTIONS.find((p) => p.value === program.period)?.days ?? 90
    );
    const [settlementCycleLength, setSettlementCycleLength] = useState(program.settlementCycleLength ?? 14);
    const [settlementCycleUnit, setSettlementCycleUnit] = useState<"days" | "weeks">(
        program.settlementCycleUnit ?? "days"
    );
    const [policies, setPolicies] = useState<Policy[]>(program.policies);
    const [policyValues, setPolicyValues] = useState<Record<string, number | string>>(() => ({
        ...Object.fromEntries(
            program.policies.flatMap((p) => {
                if (p.kind === "slider" || p.kind === "select") {
                    return [[p.id, p.defaultValue] as [string, number | string]];
                }
                if (p.kind === "tranche") {
                    return [[p.id, serializeTranches(p.defaultTranches ?? [])] as [string, number | string]];
                }
                return [];
            })
        ),
        // Program-specific overrides win over the template defaults.
        ...(program.policyValues ?? {})
    }));
    const [budget, setBudget] = useState(program.budget);

    // Attach a newly-created policy (draft) — the ProgramPolicies section
    // owns the editor UI; these mutate the lifted policies/policyValues that
    // feed the BuilderConfig.
    function addPolicy(policy: Policy, value: number | string): void {
        setPolicies((prev) => [...prev, policy]);
        setPolicyValues((prev) => ({ ...prev, [policy.id]: value }));
    }

    function removePolicy(id: string): void {
        setPolicies((prev) => prev.filter((p) => p.id !== id));
    }

    // Per-policy value edit (active/ended programs) — change a policy's
    // configured value in place without adding or removing policies.
    function setPolicyValue(id: string, value: number | string): void {
        setPolicyValues((prev) => ({ ...prev, [id]: value }));
    }

    // The single seam where draft state leaves the builder — the assembly
    // the confirm bar's onPrimary used to inline.
    //
    // `regions` / `target` were removed from the Configuration UI but the
    // org-API's required policies still expect them, so — like the
    // `client-added-funding-sources` flag — they're sent as sensible hardcoded
    // defaults (global region, agent target) rather than dropped. Open-budget /
    // continuous programs with the variable earned APY carry a 0 base APY.
    //
    // Funding is DERIVED from the Budget Type (Variable earned budget → rewards
    // fund distributions; Fixed budget / Base APY → the client sends the amount
    // to its Solomon-generated address) and shown as a read-only Configuration
    // field. It is not a stored choice and never rode into BuilderConfig / the
    // backend — the `client-added-funding-sources` required policy stays
    // hardcoded `allowed: true` regardless (see
    // `useActivateClientProgram.requiredPolicyParametersForConfig`).
    //
    // `rewardsPct` (the "rewards-model" required policy's share-to-users) is
    // the backend home for the new Variable "User %" field: a Variable/earned
    // budget sends the user's configured `userPct`; every other budget shape
    // keeps the full 100% share. NOTE: this reuses the existing rewards-model
    // `rewardsPct` slot — there's no dedicated "revenue split / User %" field
    // in the org-API contract, so it's an approximation (mirrors how region /
    // target / client-added-funding-sources are handled).
    const rewardsShare =
        programType === "fixed" && budgetMode === "fixed"
            ? DEFAULT_REWARDS_PCT
            : apyMode === "base"
              ? DEFAULT_REWARDS_PCT
              : userPct;
    function toBuilderConfig(): BuilderConfig {
        return {
            name,
            regions: [DEFAULT_REGION],
            target: DEFAULT_TARGET,
            programType,
            budgetMode,
            apyMode,
            baseApy: apyMode === "base" ? baseApy : 0,
            rewardsPct: rewardsShare,
            periodDays,
            settlementCycleLength,
            settlementCycleUnit,
            budget,
            policies,
            policyValues
        };
    }

    return {
        name,
        setName,
        programType,
        setProgramType,
        budgetMode,
        setBudgetMode,
        apyMode,
        setApyMode,
        baseApy,
        setBaseApy,
        userPct,
        setUserPct,
        periodDays,
        setPeriodDays,
        settlementCycleLength,
        setSettlementCycleLength,
        settlementCycleUnit,
        setSettlementCycleUnit,
        budget,
        setBudget,
        policies,
        policyValues,
        addPolicy,
        removePolicy,
        setPolicyValue,
        toBuilderConfig
    };
}
